import React, { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import PhoneR3FSafe from "./hero3d/PhoneR3FSafe";

/**
 * Shared cinematic deep-dive shell.
 * Config drives the hero, palette, chapters, and a per-feature "accent" React node
 * that renders inside the sticky column alongside the 3D phone.
 */

function ChapterCard({ chapter, index }) {
    const reduced = useReducedMotion();
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], reduced ? [0, 0] : [80, -80]);
    const opacity = useTransform(scrollYProgress, [0.05, 0.25, 0.75, 0.95], [0, 1, 1, 0]);

    return (
        <section
            ref={ref}
            className="relative min-h-[80vh] flex items-center px-6 lg:px-12"
            data-testid={`chapter-${index + 1}`}
        >
            <motion.div style={{ y, opacity }} className="max-w-xl">
                <p className="text-[11px] uppercase tracking-[0.32em] text-femcare-pinkDeep/80 font-bold mb-4">
                    {chapter.eyebrow}
                </p>
                <div className="flex items-center gap-3 mb-5">
                    <span
                        className="w-12 h-12 rounded-2xl flex items-center justify-center"
                        style={{ background: chapter.iconBg || "linear-gradient(135deg,#FCE4EC,#F9A8D4)" }}
                    >
                        <chapter.Icon className="w-6 h-6 text-femcare-pinkDeep" />
                    </span>
                </div>
                <h2 className="text-4xl lg:text-5xl font-extrabold text-femcare-ink leading-[1.05] tracking-[-0.03em]">
                    {chapter.title}
                </h2>
                <p className="mt-5 text-base lg:text-lg text-femcare-inkSoft leading-relaxed">
                    {chapter.body}
                </p>
            </motion.div>
        </section>
    );
}

export default function FeatureDeepDive({ config }) {
    const reduced = useReducedMotion();
    const containerRef = useRef(null);
    const scrollProgress = useRef(0);

    const { scrollYProgress } = useScroll({ target: containerRef });
    React.useEffect(() => {
        const unsub = scrollYProgress.on("change", (v) => { scrollProgress.current = v; });
        return () => unsub();
    }, [scrollYProgress]);

    React.useEffect(() => {
        const prev = document.body.style.background;
        document.body.style.background = config.bodyGradient;
        return () => { document.body.style.background = prev; };
    }, [config.bodyGradient]);

    const AccentWidget = config.Accent;

    return (
        <div
            ref={containerRef}
            data-testid={`feature-${config.slug}-page`}
            className="relative overflow-x-hidden"
            style={{ background: config.pageBg }}
        >
            {/* HERO */}
            <section className="relative pt-32 pb-16 px-6 lg:px-12 overflow-hidden">
                <div aria-hidden className="absolute inset-0 pointer-events-none">
                    {config.pearls.map((p, i) => (
                        <motion.div
                            key={i}
                            className="absolute rounded-full"
                            style={{
                                width: p.s, height: p.s, top: p.t, left: p.l,
                                background: p.grad || "radial-gradient(circle at 32% 28%, #ffffff 0%, #fce4ec 30%, #d4a0c0 100%)",
                                boxShadow: `inset -${p.s * 0.08}px -${p.s * 0.12}px ${p.s * 0.2}px rgba(255,255,255,0.5), 0 ${p.s * 0.15}px ${p.s * 0.5}px rgba(143,99,141,0.2)`,
                            }}
                            animate={reduced ? {} : { y: [0, -14, 0] }}
                            transition={{ duration: 7 + i, repeat: Infinity, ease: "easeInOut", delay: p.d || 0 }}
                        />
                    ))}
                </div>
                <div className="max-w-6xl mx-auto text-center relative z-10">
                    <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
                        className="text-[11px] uppercase tracking-[0.32em] text-femcare-pinkDeep font-bold mb-6">
                        {config.eyebrow}
                    </motion.p>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-femcare-ink leading-[1.02] tracking-[-0.035em]">
                        {config.title.line1}
                        <br />
                        <span style={{
                            background: config.headlineGradient,
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                        }}>
                            {config.title.line2}
                        </span>
                    </motion.h1>
                    <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1 }}
                        className="mt-6 text-lg text-femcare-inkSoft max-w-xl mx-auto leading-relaxed">
                        {config.subtitle}
                    </motion.p>
                </div>
            </section>

            {/* STORY GRID */}
            <section className="relative">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="hidden lg:block">
                        <div className="sticky top-24 h-[calc(100vh-6rem)] flex items-center justify-center relative">
                            <div className="w-full h-[600px] max-w-[420px] relative">
                                <PhoneR3FSafe screenSrc={config.screenSrc} scrollProgress={scrollProgress} />
                                {AccentWidget && <AccentWidget reduced={reduced} scrollProgress={scrollProgress} />}
                            </div>
                        </div>
                    </div>
                    <div>
                        {config.chapters.map((c, i) => (
                            <ChapterCard key={i} chapter={c} index={i} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Mobile phone teaser */}
            <section className="lg:hidden px-6 pb-16">
                <div className="w-full h-[560px] relative">
                    <PhoneR3FSafe screenSrc={config.screenSrc} forceFallback />
                </div>
            </section>

            {/* CLOSING CTA */}
            <section className="px-6 lg:px-12 pt-8 pb-32">
                <div
                    className="max-w-4xl mx-auto rounded-[2rem] p-10 lg:p-16 text-center text-white relative overflow-hidden"
                    style={{ background: config.ctaGradient }}
                    data-testid={`${config.slug}-cta`}
                >
                    <p className="font-script text-xl md:text-2xl text-white/85 mb-3">{config.cta.script}</p>
                    <h2 className="text-4xl md:text-5xl font-extrabold leading-[1.05] tracking-[-0.03em]">
                        {config.cta.title1}
                        <br />
                        <span className="italic font-light">{config.cta.title2}</span>
                    </h2>
                    <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                        <a
                            href="/app/signup"
                            className="inline-flex items-center gap-2 bg-white text-femcare-purple rounded-full px-7 py-3.5 font-semibold hover:scale-105 transition-transform"
                            data-testid={`${config.slug}-cta-primary`}
                        >
                            Get FemCare
                            <ArrowRight className="w-4 h-4" />
                        </a>
                        <a
                            href="/features"
                            className="inline-flex items-center gap-2 rounded-full px-6 py-3.5 font-semibold text-white border border-white/40 hover:bg-white/10 transition"
                            data-testid={`${config.slug}-cta-secondary`}
                        >
                            Explore all features
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
}
